import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";

const SECTORS = ["IT & Software", "Journalism", "Construction", "Sales", "Hospitality & Cooking", "Household Help", "Healthcare", "Design", "Education", "Logistics", "Finance", "Retail", "Other"];

const genThumbnail = (file) => new Promise((resolve) => {
  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;
  video.src = URL.createObjectURL(file);
  const timer = setTimeout(() => resolve(null), 8000);
  video.onloadeddata = () => { video.currentTime = Math.min(1, (video.duration || 2) / 2); };
  video.onseeked = () => {
    try {
      const c = document.createElement("canvas");
      c.width = video.videoWidth || 720;
      c.height = video.videoHeight || 1280;
      c.getContext("2d").drawImage(video, 0, 0, c.width, c.height);
      c.toBlob((b) => { clearTimeout(timer); URL.revokeObjectURL(video.src); resolve(b); }, "image/jpeg", 0.7);
    } catch { clearTimeout(timer); resolve(null); }
  };
  video.onerror = () => { clearTimeout(timer); resolve(null); };
});

export default function Upload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [sector, setSector] = useState(user?.sector || "");
  const [loading, setLoading] = useState(false);

  if (user?.role !== "candidate") {
    return (
      <div className="max-w-md mx-auto p-12 text-center">
        <p className="text-[#68706B]">Only candidates can upload a visual resume.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a video first");
    if (file.size > 60 * 1024 * 1024) return toast.error("Video must be under 60 MB");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("description", desc);
      fd.append("hashtags", hashtags);
      fd.append("sector", sector);
      const thumb = await genThumbnail(file);
      if (thumb) fd.append("thumbnail", thumb, "thumb.jpg");
      const { data } = await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.moderation_status === "rejected") {
        toast.error("Rejected: " + (data.moderation_reason || "non-professional content"));
      } else if (data.moderation_status === "flagged") {
        toast.warning("Flagged for review: " + (data.moderation_reason || "queued"));
      } else {
        toast.success("Published");
      }
      navigate("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 md:p-10">
      <h1 className="text-3xl font-bold tracking-tight">Upload visual resume</h1>
      <p className="text-sm text-[#68706B] mt-2">15–60 seconds. Show your work. Mention what you&apos;re open to. No politics, no entertainment.</p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="upload-form">
        <label className="block border-2 border-dashed border-[#1E3F33]/30 bg-[#F0EFEB] p-10 text-center cursor-pointer hover:bg-[#E8ECEB] transition-colors" data-testid="upload-dropzone">
          <UploadCloud className="w-8 h-8 mx-auto text-[#1E3F33]" strokeWidth={1.5} />
          <div className="mt-3 font-semibold">{file ? file.name : "Click to choose a video"}</div>
          <div className="text-xs text-[#68706B] mt-1">MP4/MOV/WebM • &lt; 60 MB</div>
          <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="upload-file-input" />
        </label>

        <Input placeholder="Title (e.g., Frontend dev, 4y React)" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none h-11" required data-testid="upload-title" />
        <Textarea placeholder="Short description (what you do, what you're looking for)" value={desc} onChange={(e) => setDesc(e.target.value)} className="rounded-none min-h-24" data-testid="upload-description" />
        <Input placeholder="hashtags, comma separated (react, frontend, remote)" value={hashtags} onChange={(e) => setHashtags(e.target.value)} className="rounded-none h-11" data-testid="upload-hashtags" />
        <Select value={sector} onValueChange={setSector}>
          <SelectTrigger className="rounded-none h-11" data-testid="upload-sector"><SelectValue placeholder="Sector" /></SelectTrigger>
          <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>

        <Button type="submit" disabled={loading} className="w-full h-12 rounded-none bg-[#1E3F33] hover:bg-[#152C24] text-base" data-testid="upload-submit">
          {loading ? "Uploading & moderating…" : "Publish"}
        </Button>
        <p className="text-xs text-[#68706B] text-center">Your upload is reviewed by AI before going live. Limit: 2 uploads per day.</p>
      </form>
    </div>
  );
}
